// 侧栏「我的文件夹」—— 参考 QQ 邮箱：挂在「收件箱」下面，可折叠，列出各文件夹 + 未读数，
// 末尾一个「新建文件夹」行内输入。改名/删除放在文件夹页头部（FolderHeaderActions）。
// 折叠状态是本设备视图偏好，存 localStorage（读写都 try/catch，隐私模式下退化为默认展开）。

import { ChevronRight, Folder, FolderPlus } from 'lucide-react';
import { useRef, useState } from 'react';
import { NavLink } from 'react-router-dom';

import { cn } from '../../lib/cn.js';
import { useCreateFolder, useFolders } from '../../lib/folders.js';

const COLLAPSE_KEY = 'aliashub.nav.foldersCollapsed';

function readCollapsed(): boolean {
  try {
    return localStorage.getItem(COLLAPSE_KEY) === '1';
  } catch {
    return false;
  }
}

function writeCollapsed(v: boolean): void {
  try {
    localStorage.setItem(COLLAPSE_KEY, v ? '1' : '0');
  } catch {
    // 忽略：只是视图偏好
  }
}

export function FolderNav() {
  const q = useFolders();
  const create = useCreateFolder();
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const folders = q.data?.items ?? [];

  const toggle = () => {
    setCollapsed((v) => {
      writeCollapsed(!v);
      return !v;
    });
  };

  // 回车提交与失焦提交会先后触发：不挡住的话同名文件夹会被提交两次，第二次撞「已存在」，
  // 而 mutate() 的调用级 onSuccess 只对最后一次调用生效 —— 首次成功也关不掉输入框。
  const inFlight = useRef(false);
  // Esc 取消后输入框卸载，个别浏览器仍会补发 blur，别把取消变成创建
  const cancelled = useRef(false);

  const closeInput = () => {
    setName('');
    setAdding(false);
    create.reset();
  };

  const openInput = () => {
    cancelled.current = false;
    create.reset();
    setAdding(true);
  };

  const submit = () => {
    if (inFlight.current || cancelled.current) return;
    const n = name.trim();
    if (!n) {
      closeInput();
      return;
    }
    inFlight.current = true;
    create.mutate(n, {
      onSuccess: closeInput,
      onSettled: () => {
        inFlight.current = false;
      },
    });
  };

  return (
    <div>
      <div className="group flex min-h-[40px] items-center rounded-lg text-sm text-slate-600 dark:text-slate-300">
        <button
          type="button"
          onClick={toggle}
          aria-expanded={!collapsed}
          className="flex flex-1 items-center gap-3 rounded-lg px-3 py-2 hover:bg-slate-100 dark:hover:bg-slate-900 whitespace-nowrap"
        >
          <ChevronRight
            className={cn('h-4 w-4 shrink-0 transition-transform', collapsed ? '' : 'rotate-90')}
          />
          <span className="flex-1 text-left">我的文件夹</span>
        </button>
        <button
          type="button"
          onClick={() => {
            openInput();
            if (collapsed) toggle();
          }}
          aria-label="新建文件夹"
          className="relative mr-1 inline-flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-brand-600 dark:hover:bg-slate-900"
        >
          <FolderPlus className="h-4 w-4" />
        </button>
      </div>

      {collapsed ? null : (
        <div className="space-y-0.5">
          {folders.map((f) => (
            <NavLink
              key={f.id}
              to={`/app/folders/${String(f.id)}`}
              className={({ isActive }) =>
                cn(
                  'flex min-h-[40px] items-center gap-3 rounded-lg py-2 pl-10 pr-3 text-sm transition-colors whitespace-nowrap',
                  isActive
                    ? 'bg-brand-50 text-brand-700 dark:bg-brand-500/10 dark:text-brand-300'
                    : 'text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-900',
                )
              }
            >
              <Folder className="h-4 w-4 shrink-0" />
              <span className="min-w-0 flex-1 truncate">{f.name}</span>
              {f.unreadCount > 0 ? (
                <span className="rounded-md bg-brand-100 px-1.5 py-0.5 text-[10px] font-medium text-brand-700 dark:bg-brand-500/20 dark:text-brand-200">
                  {f.unreadCount > 99 ? '99+' : f.unreadCount}
                </span>
              ) : null}
            </NavLink>
          ))}

          {adding ? (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                submit();
              }}
              className="py-1 pl-10 pr-3"
            >
              <input
                autoFocus
                value={name}
                maxLength={32}
                onChange={(e) => {
                  setName(e.target.value);
                  if (create.isError) create.reset();
                }}
                onBlur={submit}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    cancelled.current = true;
                    closeInput();
                  }
                }}
                placeholder="文件夹名，回车创建"
                // readOnly 而非 disabled：disabled 会让输入框失焦，触发 onBlur 再提交一次
                readOnly={create.isPending}
                className="h-8 w-full rounded-md border border-slate-200 bg-white px-2 text-sm outline-none focus:border-brand-400 dark:border-slate-700 dark:bg-slate-950"
              />
              {create.isError ? (
                <p className="mt-1 text-[11px] text-red-600 dark:text-red-400">{create.error.message}</p>
              ) : null}
            </form>
          ) : folders.length === 0 && !q.isLoading ? (
            <button
              type="button"
              onClick={openInput}
              className="flex min-h-[36px] w-full items-center py-1.5 pl-10 pr-3 text-left text-xs text-slate-400 hover:text-brand-600 whitespace-nowrap"
            >
              + 新建文件夹
            </button>
          ) : null}
        </div>
      )}
    </div>
  );
}
